/**
 * 進階搜尋的「最近搜尋」紀錄(REQ-20261027001)。
 *
 * 存 localStorage,key 帶登入者 id:要跨工作階段保留(關掉分頁、隔天再來都還在),
 * 但共用電腦換人登入時看不到前一個人的紀錄。
 * 讀寫失敗(隱私模式 / quota 超限 / 內容被改壞)一律當成沒有紀錄,不影響搜尋本身 ——
 * 寫法比照 components/CustomersNavLink.tsx 的 sessionStorage helper。
 */
export interface SearchHistoryItem<F extends string> {
  field: F;
  q: string;
}

const MAX_ITEMS = 10;

function storageKey(userId: number): string {
  return `customer-search-history:${userId}`;
}

/** `fields` 是目前還存在的欄位;欄位日後改名 / 拿掉時舊紀錄直接濾掉,不讓它打壞畫面 */
export function readSearchHistory<F extends string>(
  userId: number,
  fields: readonly F[],
): SearchHistoryItem<F>[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (h): h is SearchHistoryItem<F> => typeof h?.q === 'string' && fields.includes(h.field),
    );
  } catch {
    return [];
  }
}

/** 把這筆移到最前面(同欄位 + 同關鍵字算同一筆),超過 10 筆截掉最舊的;回傳新清單 */
export function pushSearchHistory<F extends string>(
  userId: number,
  fields: readonly F[],
  item: SearchHistoryItem<F>,
): SearchHistoryItem<F>[] {
  // 從 storage 重讀而不是沿用畫面上的清單 —— 另一個分頁剛搜過的才不會被蓋掉
  const rest = readSearchHistory(userId, fields).filter(
    (h) => h.field !== item.field || h.q !== item.q,
  );
  const next = [item, ...rest].slice(0, MAX_ITEMS);
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // 存不進去 → 靜默失敗,畫面上這次的清單照樣更新
  }
  return next;
}

export function clearSearchHistory(userId: number): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    /* noop */
  }
}
